import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged, updateProfile, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import {
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Avatar,
  Box,
  Divider,
  Chip,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EmailIcon from '@mui/icons-material/Email';
import WorkIcon from '@mui/icons-material/Work';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: theme.spacing(12),
  height: theme.spacing(12),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
}));

const InfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const Account = () => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editingDisplayName, setEditingDisplayName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState('');

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        fetchUserData(currentUser.uid);
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchUserData = async (uid) => {
    try {
      const userDocRef = doc(db, 'users', uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const data = userDocSnap.data();
        setUserData(data);
        setNewDisplayName(data.displayName || '');
      } else {
        setSnackbar({ open: true, message: 'User data not found', severity: 'error' });
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setSnackbar({ open: true, message: 'Error fetching user data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleEditDisplayName = () => {
    setEditingDisplayName(true);
  };

  const handleSaveDisplayName = async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (user) {
        // Update display name in Firebase Auth
        await updateProfile(user, { displayName: newDisplayName });

        // Update display name in Firestore
        const userDocRef = doc(db, 'users', user.uid);
        await updateDoc(userDocRef, { displayName: newDisplayName });

        // Update local state
        setUserData({ ...userData, displayName: newDisplayName });
        setEditingDisplayName(false);
        setSnackbar({ open: true, message: 'Display name updated successfully', severity: 'success' });
      }
    } catch (error) {
      console.error('Error updating display name:', error);
      setSnackbar({ open: true, message: 'Error updating display name', severity: 'error' });
    }
  };

  const handlePasswordReset = async () => {
    const auth = getAuth();
    if (user) {
      try {
        await sendPasswordResetEmail(auth, user.email);
        setSnackbar({ open: true, message: 'Password reset email sent', severity: 'success' });
      } catch (error) {
        console.error('Error sending password reset email:', error);
        setSnackbar({ open: true, message: 'Error sending password reset email', severity: 'error' });
      }
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="sm">
        <Typography variant="h5" component="h1" gutterBottom>
          Please log in to view your account.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <StyledPaper elevation={3}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <StyledAvatar>
            <AccountCircleIcon fontSize="large" />
          </StyledAvatar>
          <Typography variant="h4" component="h1" gutterBottom>
            Account Information
          </Typography>
        </Box>
        <Divider sx={{ my: 3 }}>
          <Chip label="Personal Details" />
        </Divider>
        <InfoItem>
          <EmailIcon sx={{ mr: 2, color: 'primary.main' }} />
          <TextField
            fullWidth
            label="Email"
            value={user.email}
            InputProps={{ readOnly: true }}
            variant="outlined"
          />
        </InfoItem>
        <InfoItem>
          <AccountCircleIcon sx={{ mr: 2, color: 'primary.main' }} />
          <TextField
            fullWidth
            label="Display Name"
            value={editingDisplayName ? newDisplayName : (userData?.displayName || '')}
            onChange={(e) => setNewDisplayName(e.target.value)}
            InputProps={{
              readOnly: !editingDisplayName,
              endAdornment: (
                <IconButton
                  onClick={editingDisplayName ? handleSaveDisplayName : handleEditDisplayName}
                  edge="end"
                >
                  {editingDisplayName ? <SaveIcon /> : <EditIcon />}
                </IconButton>
              ),
            }}
            variant="outlined"
          />
        </InfoItem>
        <InfoItem>
          <WorkIcon sx={{ mr: 2, color: 'primary.main' }} />
          <TextField
            fullWidth
            label="Role"
            value={userData?.role || ''}
            InputProps={{ readOnly: true }}
            variant="outlined"
          />
        </InfoItem>
        <InfoItem>
          <VerifiedUserIcon sx={{ mr: 2, color: 'primary.main' }} />
          <TextField
            fullWidth
            label="Status"
            value={userData?.status || ''}
            InputProps={{ readOnly: true }}
            variant="outlined"
          />
        </InfoItem>
        <InfoItem>
          <Button variant="outlined" onClick={handlePasswordReset}>
            Reset Password
          </Button>
        </InfoItem>
      </StyledPaper>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Account;

